// src/pages/affiliate/RegisterPage.tsx
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useUI } from '../../context/UIContext';
import { getBaseUrl } from '../../config/api';
import { validatePhone, validateZipCode, formatPhoneNumber } from '../../utils/validation';
import { US_STATES } from '../../constants/usStates';
import TeslaSelect from '../../components/common/TeslaSelect';
import '../../styles/AffiliateRegister.css';

// Password requirements
const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  minLowercase: 1,
  minUppercase: 1,
  minNumbers: 1,
  minSpecialChars: 1,
  allowedSpecialChars: '!@#$%^&*',
};

// Platform URL patterns
const PLATFORM_URL_PATTERNS: Record<string, (handle: string) => string> = {
  'Instagram': (handle) => {
    const cleanHandle = handle.replace(/^@/, '').replace(/\s+/g, '');
    return `https://instagram.com/${cleanHandle}`;
  },
  'TikTok': (handle) => {
    const cleanHandle = handle.replace(/^@/, '').replace(/\s+/g, '');
    return `https://tiktok.com/@${cleanHandle}`;
  },
  'Facebook': (handle) => {
    const cleanHandle = handle.replace(/^@/, '').replace(/\s+/g, '');
    return `https://facebook.com/${cleanHandle}`;
  },
  'YouTube': (handle) => {
    const cleanHandle = handle.replace(/^@/, '').replace(/\s+/g, '');
    return `https://youtube.com/@${cleanHandle}`;
  },
  'Twitter/X': (handle) => {
    const cleanHandle = handle.replace(/^@/, '').replace(/\s+/g, '');
    return `https://x.com/${cleanHandle}`;
  },
  'LinkedIn': (handle) => {
    const cleanHandle = handle.replace(/^@/, '').replace(/\s+/g, '');
    return `https://linkedin.com/in/${cleanHandle}`;
  },
  'Other': (handle) => {
    const cleanHandle = handle.replace(/^@/, '').replace(/\s+/g, '');
    return `https://${cleanHandle}`;
  }
};

// Platform URL placeholders
const PLATFORM_URL_PLACEHOLDERS: Record<string, string> = {
  'Instagram': 'https://instagram.com/yourhandle',
  'TikTok': 'https://tiktok.com/@yourhandle',
  'Facebook': 'https://facebook.com/yourhandle',
  'YouTube': 'https://youtube.com/@yourhandle',
  'Twitter/X': 'https://x.com/yourhandle',
  'LinkedIn': 'https://linkedin.com/in/yourhandle',
  'Other': 'https://yourwebsite.com'
};

interface SocialMediaAccount {
  platform: string;
  handle: string;
  followerCount: number;
  profileUrl: string;
}

interface FormData {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  socialMedia: SocialMediaAccount[];
  agreedToTerms: boolean;
}

const AffiliateRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackbar, showLoading, hideLoading } = useUI();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US'
    },
    socialMedia: [{
      platform: 'Instagram',
      handle: '',
      followerCount: 0,
      profileUrl: ''
    }],
    agreedToTerms: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Password strength checking
  const getPasswordStrength = (password: string) => {
    const checks = {
      minLength: password.length >= PASSWORD_REQUIREMENTS.minLength,
      lowercase: (password.match(/[a-z]/g) || []).length >= PASSWORD_REQUIREMENTS.minLowercase,
      uppercase: (password.match(/[A-Z]/g) || []).length >= PASSWORD_REQUIREMENTS.minUppercase,
      number: (password.match(/\d/g) || []).length >= PASSWORD_REQUIREMENTS.minNumbers,
      specialChar: (password.match(new RegExp(`[${PASSWORD_REQUIREMENTS.allowedSpecialChars}]`, 'g')) || []).length >= PASSWORD_REQUIREMENTS.minSpecialChars,
    };
    return checks;
  };

  // Format phone number as user types
  const handlePhoneChange = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 10) {
      const formatted = formatPhoneNumber(digits);
      updateField('phone', formatted);
    }
  };

  // Format zip code (only allow 5 digits)
  const handleZipCodeChange = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 5) {
      updateField('address.zipCode', digits);
    }
  };

  const updateField = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev as any)[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Add new social media account
  const addSocialMediaAccount = (platform: string = 'Instagram') => {
    // Check if platform already exists
    const exists = formData.socialMedia.some(acc => acc.platform === platform);
    if (exists) return;
    
    setFormData(prev => ({
      ...prev,
      socialMedia: [...prev.socialMedia, {
        platform: platform,
        handle: '',
        followerCount: 0,
        profileUrl: ''
      }]
    }));
  };

  // Remove social media account
  const removeSocialMediaAccount = (index: number) => {
    if (formData.socialMedia.length > 1) {
      setFormData(prev => ({
        ...prev,
        socialMedia: prev.socialMedia.filter((_, i) => i !== index)
      }));
    }
  };

  // Update social media account field
  const updateSocialMediaField = (index: number, field: keyof SocialMediaAccount, value: any) => {
    setFormData(prev => {
      const updated = prev.socialMedia.map((account, i) => {
        if (i === index) {
          const updatedAccount = { ...account, [field]: value };
          
          // Auto-generate Profile URL when handle changes (if URL is empty or matches old pattern)
          if (field === 'handle' && typeof value === 'string' && value.trim()) {
            const platform = account.platform;
            const urlGenerator = PLATFORM_URL_PATTERNS[platform];
            
            // Only auto-generate if URL is empty or matches the old handle pattern
            if (!updatedAccount.profileUrl || 
                updatedAccount.profileUrl === account.profileUrl ||
                (account.handle && updatedAccount.profileUrl.includes(account.handle))) {
              if (urlGenerator) {
                updatedAccount.profileUrl = urlGenerator(value);
              }
            }
          }
          
          return updatedAccount;
        }
        return account;
      });
      
      return { ...prev, socialMedia: updated };
    });
    // Clear error
    const errorKey = `socialMedia.${index}.${field}`;
    if (errors[errorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // Generate URL from handle for a specific platform
  const generateProfileUrl = (index: number) => {
    const account = formData.socialMedia[index];
    if (!account.handle.trim()) {
      return;
    }
    
    const urlGenerator = PLATFORM_URL_PATTERNS[account.platform];
    if (urlGenerator) {
      updateSocialMediaField(index, 'profileUrl', urlGenerator(account.handle));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      else if (!/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = 'Invalid email format';
      
      // Password validation
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else {
        const strength = getPasswordStrength(formData.password);
        if (!strength.minLength) newErrors.password = 'Password must be at least 8 characters';
        else if (!strength.lowercase) newErrors.password = 'Password must contain at least one lowercase letter';
        else if (!strength.uppercase) newErrors.password = 'Password must contain at least one uppercase letter';
        else if (!strength.number) newErrors.password = 'Password must contain at least one number';
        else if (!strength.specialChar) newErrors.password = `Password must contain at least one special character (${PASSWORD_REQUIREMENTS.allowedSpecialChars})`;
      }
      
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      
      // Phone validation
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else {
        const cleanPhone = formData.phone.replace(/\D/g, '');
        if (cleanPhone.length !== 10) {
          newErrors.phone = 'Phone number must be 10 digits';
        } else if (!validatePhone(formData.phone)) {
          newErrors.phone = 'Invalid phone number format';
        }
      }
    } else if (step === 2) {
      if (!formData.address.city.trim()) newErrors['address.city'] = 'City is required';
      if (!formData.address.state.trim()) newErrors['address.state'] = 'State is required';
      
      // Zip code validation
      if (!formData.address.zipCode.trim()) {
        newErrors['address.zipCode'] = 'Zip code is required';
      } else if (!validateZipCode(formData.address.zipCode)) {
        newErrors['address.zipCode'] = 'Zip code must be exactly 5 digits';
      }
    } else if (step === 3) {
      // Validate all social media accounts
      formData.socialMedia.forEach((account, index) => {
        if (!account.handle.trim()) {
          newErrors[`socialMedia.${index}.handle`] = 'Social media handle is required';
        }
        if (!account.followerCount || account.followerCount < 0) {
          newErrors[`socialMedia.${index}.followerCount`] = 'Valid follower count is required';
        } else if (account.followerCount < 1000) {
          newErrors[`socialMedia.${index}.followerCount`] = 'Minimum 1,000 followers required';
        }
      });
    } else if (step === 4) {
      if (!formData.agreedToTerms) newErrors.agreedToTerms = 'You must agree to the terms';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep(4)) return;

    try {
      showLoading('Submitting your application...');

      // Clean phone number before sending
      const cleanPhone = formData.phone.replace(/\D/g, '');

      // Send the first social media account as primary (backend expects single object)
      // Additional accounts can be stored separately if backend is updated
      const primarySocialMedia = formData.socialMedia[0] || formData.socialMedia;

      const response = await fetch(`${getBaseUrl()}/v1/affiliate/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          phone: cleanPhone,
          socialMedia: primarySocialMedia,
          // Include all social media accounts for potential future use
          socialMediaAccounts: formData.socialMedia.length > 1 ? formData.socialMedia : undefined
        })
      });

      const data = await response.json();

      hideLoading();

      if (response.ok && data.success) {
        showSnackbar('Registration successful! Please check your email to verify your account.', 'success');
        navigate('/verify-email', { 
          state: { 
            email: formData.email,
            name: formData.fullName,
            userType: 'affiliate'
          } 
        });
      } else {
        showSnackbar(data.message || 'Registration failed', 'error');
      }
    } catch (error) {
      hideLoading();
      console.error('Registration error:', error);
      showSnackbar('An error occurred during registration', 'error');
    }
  };

  const renderStep1 = () => {
    const passwordStrength = formData.password ? getPasswordStrength(formData.password) : null;

    return (
      <div className="affiliate-register__step">
        <h2>Personal Information</h2>
        <p className="affiliate-register__step-description">Let's start with your basic information</p>

        <div className="affiliate-register__form-group">
          <label htmlFor="fullName">Full Name *</label>
          <input
            id="fullName"
            type="text"
            value={formData.fullName}
            onChange={(e) => updateField('fullName', e.target.value)}
            placeholder="John Doe"
            className={errors.fullName ? 'error' : ''}
          />
          {errors.fullName && <span className="error-message">{errors.fullName}</span>}
        </div>

        <div className="affiliate-register__form-group">
          <label htmlFor="email">Email Address *</label>
          <input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => updateField('email', e.target.value)}
            placeholder="john@example.com"
            className={errors.email ? 'error' : ''}
          />
          {errors.email && <span className="error-message">{errors.email}</span>}
        </div>

        <div className="affiliate-register__form-group">
          <label htmlFor="password">Password *</label>
          <input
            id="password"
            type="password"
            value={formData.password}
            onChange={(e) => updateField('password', e.target.value)}
            placeholder="Min. 8 characters"
            className={errors.password ? 'error' : ''}
          />
          {errors.password && <span className="error-message">{errors.password}</span>}
          
          {/* Password Strength Indicator */}
          {formData.password && passwordStrength && (
            <div className="password-strength">
              <div className="password-strength__title">Password Requirements:</div>
              <div className="password-strength__checks">
                <div className={`password-check ${passwordStrength.minLength ? 'valid' : ''}`}>
                  At least 8 characters
                </div>
                <div className={`password-check ${passwordStrength.lowercase ? 'valid' : ''}`}>
                  One lowercase letter
                </div>
                <div className={`password-check ${passwordStrength.uppercase ? 'valid' : ''}`}>
                  One uppercase letter
                </div>
                <div className={`password-check ${passwordStrength.number ? 'valid' : ''}`}>
                  One number
                </div>
                <div className={`password-check ${passwordStrength.specialChar ? 'valid' : ''}`}>
                  One special character (!@#$%^&*)
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="affiliate-register__form-group">
          <label htmlFor="confirmPassword">Confirm Password *</label>
          <input
            id="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => updateField('confirmPassword', e.target.value)}
            placeholder="Re-enter password"
            className={errors.confirmPassword ? 'error' : ''}
          />
          {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
        </div>

        <div className="affiliate-register__form-group">
          <label htmlFor="phone">Phone Number *</label>
          <input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            placeholder="(555) 123-4567"
            className={errors.phone ? 'error' : ''}
            maxLength={14}
          />
          {errors.phone && <span className="error-message">{errors.phone}</span>}
          <small>Format: (XXX) XXX-XXXX</small>
        </div>
      </div>
    );
  };

  const renderStep2 = () => {
    const stateOptions = US_STATES.map(state => ({
      value: state.value,
      label: state.label
    }));

    return (
      <div className="affiliate-register__step">
        <h2>Address Information</h2>
        <p className="affiliate-register__step-description">Where are you located?</p>

        <div className="affiliate-register__form-group">
          <label htmlFor="street">Street Address</label>
          <input
            id="street"
            type="text"
            value={formData.address.street}
            onChange={(e) => updateField('address.street', e.target.value)}
            placeholder="123 Main St"
          />
        </div>

        <div className="affiliate-register__form-row">
          <div className="affiliate-register__form-group">
            <label htmlFor="city">City *</label>
            <input
              id="city"
              type="text"
              value={formData.address.city}
              onChange={(e) => updateField('address.city', e.target.value)}
              placeholder="New York"
              className={errors['address.city'] ? 'error' : ''}
            />
            {errors['address.city'] && <span className="error-message">{errors['address.city']}</span>}
          </div>

          <div className="affiliate-register__form-group">
            <TeslaSelect
              id="state"
              label="State *"
              options={stateOptions}
              value={formData.address.state}
              onChange={(value) => updateField('address.state', value)}
              placeholder="Select state"
              searchable={true}
              searchPlaceholder="Type to search states..."
              error={errors['address.state']}
              fullWidth
            />
            {errors['address.state'] && <span className="error-message">{errors['address.state']}</span>}
          </div>
        </div>

        <div className="affiliate-register__form-row">
          <div className="affiliate-register__form-group">
            <label htmlFor="zipCode">Zip Code *</label>
            <input
              id="zipCode"
              type="text"
              value={formData.address.zipCode}
              onChange={(e) => handleZipCodeChange(e.target.value)}
              placeholder="10001"
              className={errors['address.zipCode'] ? 'error' : ''}
              maxLength={5}
            />
            {errors['address.zipCode'] && <span className="error-message">{errors['address.zipCode']}</span>}
            <small>5 digits only</small>
          </div>

          <div className="affiliate-register__form-group">
            <label htmlFor="country">Country</label>
            <select
              id="country"
              value={formData.address.country}
              onChange={(e) => updateField('address.country', e.target.value)}
            >
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="UK">United Kingdom</option>
              <option value="TG">Togo</option>
            </select>
          </div>
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    const socialPlatforms = [
      { value: 'Instagram', label: 'Instagram' },
      { value: 'TikTok', label: 'TikTok' },
      { value: 'Facebook', label: 'Facebook' },
      { value: 'YouTube', label: 'YouTube' },
      { value: 'Twitter/X', label: 'Twitter/X' },
      { value: 'LinkedIn', label: 'LinkedIn' },
      { value: 'Other', label: 'Other' }
    ];

    // Get all selected platforms from existing accounts
    const selectedPlatforms = formData.socialMedia.map(acc => acc.platform);
    
    // Handle platform selection (multi-select)
    const handlePlatformToggle = (platformValue: string) => {
      const existingIndex = formData.socialMedia.findIndex(acc => acc.platform === platformValue);
      
      if (existingIndex >= 0) {
        // Platform already selected, remove it
        removeSocialMediaAccount(existingIndex);
      } else {
        // Platform not selected, add it
        addSocialMediaAccount(platformValue);
      }
    };

    return (
      <div className="affiliate-register__step">
        <h2>Social Media Presence</h2>
        <p className="affiliate-register__step-description">Select all platforms where you have a presence, then fill in details for each</p>

        {/* Multi-Select Platform Picker */}
        <div className="affiliate-register__form-group">
          <label>Select Platforms *</label>
          <div className="platform-multi-select">
            {socialPlatforms.map(platform => {
              const isSelected = selectedPlatforms.includes(platform.value);
              return (
                <div
                  key={platform.value}
                  className={`platform-chip ${isSelected ? 'selected' : ''}`}
                  onClick={() => handlePlatformToggle(platform.value)}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation(); // Prevent bubbling to div
                      handlePlatformToggle(platform.value);
                    }}
                    onClick={(e) => e.stopPropagation()} // Also prevent click from bubbling
                    className="platform-checkbox"
                  />
                  <span className="platform-label">{platform.label}</span>
                  {isSelected && <i className="fas fa-check platform-check-icon"></i>}
                </div>
              );
            })}
          </div>
          {selectedPlatforms.length === 0 && (
            <span className="error-message" style={{ display: 'block', marginTop: '0.5rem' }}>
              Please select at least one platform
            </span>
          )}
        </div>

        {/* Details for each selected platform */}
        {formData.socialMedia.map((account, index) => (
          <div key={index} className="social-media-account">
            <div className="social-media-account__header">
              <h3>{account.platform}</h3>
              <button
                type="button"
                onClick={() => removeSocialMediaAccount(index)}
                className="btn-remove"
                aria-label="Remove platform"
              >
                ×
              </button>
            </div>

            <div className="affiliate-register__form-group">
              <label htmlFor={`handle-${index}`}>Username/Handle *</label>
              <input
                id={`handle-${index}`}
                type="text"
                value={account.handle}
                onChange={(e) => updateSocialMediaField(index, 'handle', e.target.value)}
                placeholder="@yourhandle"
                className={errors[`socialMedia.${index}.handle`] ? 'error' : ''}
              />
              {errors[`socialMedia.${index}.handle`] && (
                <span className="error-message">{errors[`socialMedia.${index}.handle`]}</span>
              )}
            </div>

            <div className="affiliate-register__form-group">
              <label htmlFor={`followerCount-${index}`}>Follower Count *</label>
              <input
                id={`followerCount-${index}`}
                type="number"
                value={account.followerCount || ''}
                onChange={(e) => updateSocialMediaField(index, 'followerCount', parseInt(e.target.value) || 0)}
                placeholder="10000"
                className={errors[`socialMedia.${index}.followerCount`] ? 'error' : ''}
              />
              {errors[`socialMedia.${index}.followerCount`] && (
                <span className="error-message">{errors[`socialMedia.${index}.followerCount`]}</span>
              )}
              <small>Minimum 1,000 followers required</small>
            </div>

            <div className="affiliate-register__form-group">
              <label htmlFor={`profileUrl-${index}`}>
                Profile URL
                {account.handle && (
                  <button
                    type="button"
                    onClick={() => generateProfileUrl(index)}
                    className="btn-generate-url"
                    title="Generate URL from handle"
                  >
                    <i className="fas fa-magic"></i> Generate from Handle
                  </button>
                )}
              </label>
              <input
                id={`profileUrl-${index}`}
                type="url"
                value={account.profileUrl}
                onChange={(e) => updateSocialMediaField(index, 'profileUrl', e.target.value)}
                placeholder={PLATFORM_URL_PLACEHOLDERS[account.platform] || 'https://yourprofile.com'}
                className={errors[`socialMedia.${index}.profileUrl`] ? 'error' : ''}
              />
              {errors[`socialMedia.${index}.profileUrl`] && (
                <span className="error-message">{errors[`socialMedia.${index}.profileUrl`]}</span>
              )}
              {account.handle && !account.profileUrl && (
                <small className="url-hint">
                  <i className="fas fa-info-circle"></i> URL will auto-generate when you enter your handle, or click "Generate from Handle"
                </small>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderStep4 = () => (
    <div className="affiliate-register__step">
      <h2>Review & Confirm</h2>
      <p className="affiliate-register__step-description">Please review your information</p>

      <div className="affiliate-register__review">
        <div className="affiliate-register__review-section">
          <h3>Personal Information</h3>
          <p><strong>Name:</strong> {formData.fullName}</p>
          <p><strong>Email:</strong> {formData.email}</p>
          <p><strong>Phone:</strong> {formData.phone}</p>
        </div>

        <div className="affiliate-register__review-section">
          <h3>Address</h3>
          <p>
            {formData.address.street && `${formData.address.street}, `}
            {formData.address.city}, {formData.address.state} {formData.address.zipCode}
          </p>
        </div>

        <div className="affiliate-register__review-section">
          <h3>Social Media</h3>
          {formData.socialMedia.map((account, index) => (
            <div key={index} style={{ marginBottom: '1rem' }}>
              <p><strong>Platform {index + 1}:</strong> {account.platform}</p>
              <p><strong>Handle:</strong> {account.handle}</p>
              <p><strong>Followers:</strong> {account.followerCount.toLocaleString()}</p>
              {account.profileUrl && <p><strong>URL:</strong> {account.profileUrl}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="affiliate-register__terms">
        <label className="affiliate-register__checkbox">
          <input
            type="checkbox"
            checked={formData.agreedToTerms}
            onChange={(e) => updateField('agreedToTerms', e.target.checked)}
          />
          <span>
            I agree to the{' '}
            <Link to="/affiliate-terms" target="_blank" onClick={(e) => e.stopPropagation()}>
              Referral Agent Program Terms &amp; Conditions
            </Link>{' '}
            and{' '}
            <Link to="/privacy" target="_blank" onClick={(e) => e.stopPropagation()}>
              Privacy Policy
            </Link>
          </span>
        </label>
        {errors.agreedToTerms && <span className="error-message">{errors.agreedToTerms}</span>}
      </div>
    </div>
  );

  return (
    <div className="affiliate-register">
      <div className="affiliate-register__container">
        <div className="affiliate-register__header">
          <h1>Join the LookBel Referral Program</h1>
          <p>Earn commissions by promoting beauty services</p>
        </div>

        <div className="affiliate-register__progress">
          {[1, 2, 3, 4].map(step => (
            <div
              key={step}
              className={`affiliate-register__progress-step ${currentStep >= step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
            >
              <div className="affiliate-register__progress-circle">{step}</div>
              <span className="affiliate-register__progress-label">
                {step === 1 && 'Personal'}
                {step === 2 && 'Address'}
                {step === 3 && 'Social Media'}
                {step === 4 && 'Review'}
              </span>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="affiliate-register__form">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}

          <div className="affiliate-register__actions">
            {currentStep > 1 && (
              <button type="button" onClick={handleBack} className="affiliate-register__btn affiliate-register__btn--secondary">
                Back
              </button>
            )}
            {currentStep < 4 ? (
              <button type="button" onClick={handleNext} className="affiliate-register__btn affiliate-register__btn--primary">
                Next
              </button>
            ) : (
              <button
                type="submit"
                className="affiliate-register__btn affiliate-register__btn--primary"
                disabled={!formData.agreedToTerms}
              >
                Submit Application
              </button>
            )}
          </div>
        </form>

        <div className="affiliate-register__footer">
          <p>
            Already have an account? <Link to="/affiliate/login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AffiliateRegisterPage;
